import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AxonEvent, WsServerMessage } from '@reflex/shared';
import { deduplicateEvents } from './event-utils';
import { usePluginSocket } from './PluginClientContext';

export type WsConnectionState = 'connecting' | 'open' | 'closed';
export type WsMessageHandler = (msg: WsServerMessage) => void;

export function useSocketState(): WsConnectionState {
  const socket = usePluginSocket();
  return useSyncExternalStore(
    (callback) => socket.onConnectionChange(callback),
    () => (socket.connected ? 'open' : 'closed'),
  );
}

export function useSocketConnection(): WsConnectionState {
  return useSocketState();
}

export function useSocketMessage(handler: WsMessageHandler): void {
  const socket = usePluginSocket();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    return socket.onMessage((message) => handlerRef.current(message));
  }, [socket]);
}

/**
 * Subscribe to a plugin's generic real-time pushes (the server-side
 * counterpart is `ctx.services.broadcastService.broadcastPluginEvent`).
 * Fires `handler` with the raw `payload` for every `plugin_event` message
 * whose `pluginName` and `eventType` match. The payload is plugin-owned
 * and arrives as `unknown` — narrow it (e.g. with a Zod schema) in the
 * handler. Broadcasts are already org-scoped server-side, so only events
 * for the caller's active org arrive here.
 */
export function usePluginEvent(
  pluginName: string,
  eventType: string,
  handler: (payload: unknown) => void,
): void {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });

  useSocketMessage((msg) => {
    if (msg.type !== 'plugin_event') return;
    if (msg.pluginName !== pluginName || msg.eventType !== eventType) return;
    handlerRef.current(msg.payload);
  });
}

export function useSocketSubscription(streamId: string | null): void {
  const socket = usePluginSocket();

  useEffect(() => {
    if (!streamId) return;
    socket.subscribe(streamId);
    return () => socket.unsubscribe(streamId);
  }, [socket, streamId]);
}

export function useSocketReconnectCallback(callback: () => void): void {
  const socket = usePluginSocket();
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  });

  useEffect(() => {
    if (socket.addReconnectCallback && socket.removeReconnectCallback) {
      const handler = () => callbackRef.current();
      socket.addReconnectCallback(handler);
      return () => socket.removeReconnectCallback?.(handler);
    }

    let hasOpened = socket.connected;
    return socket.onConnectionChange((connected) => {
      if (!connected) return;
      if (hasOpened) {
        callbackRef.current();
        return;
      }
      hasOpened = true;
    });
  }, [socket]);
}

interface UseStreamSocketOptions {
  streamId: string | null;
  queryKey: readonly unknown[];
}

export function useStreamSocket({ streamId, queryKey }: UseStreamSocketOptions): void {
  const queryClient = useQueryClient();

  useSocketSubscription(streamId);

  useSocketMessage((msg) => {
    if (msg.type !== 'event' || !streamId) return;
    if (msg.event.streamId !== streamId) return;

    queryClient.setQueryData<AxonEvent[]>(queryKey, (old: AxonEvent[] | undefined) =>
      deduplicateEvents([...(old ?? []), msg.event]),
    );
  });
}

export function getSocketManager() {
  return {
    subscribe(_streamId: string) {
      return undefined;
    },
    unsubscribe(_streamId: string) {
      return undefined;
    },
    addReconnectCallback(_callback: () => void) {
      return undefined;
    },
    removeReconnectCallback(_callback: () => void) {
      return undefined;
    },
  };
}
